import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { OpsTask, OpsCategory, CRMUser } from '../../types';
import StatusPill from '../pm/StatusPill';
import { ChevronDown, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { getOpsTaskSchedule, stripOpsDay } from '../../utils/opsTaskSchedule';
import { buildGanttDays, computeGanttDateRangeMs, ganttFitCellWidth, ganttFitViewportChangedEnough } from '../../utils/ganttDateRange';
import './OpsGantt.css';

type ZoomLevel = 'day' | 'week' | 'month' | 'fit';

const ZOOM_CONFIGS: Record<Exclude<ZoomLevel, 'fit'>, { cellWidth: number }> = {
  day: { cellWidth: 36 },
  week: { cellWidth: 18 },
  month: { cellWidth: 6 },
};

const FIT_CELL_MIN = 4;
const FIT_CELL_MAX = 120;
const ZOOM_ANIM_MS = 170;

const CATEGORY_LABELS: Record<OpsCategory, string> = {
  admin: 'Admin',
  maintenance: 'Maintenance',
  ordering: 'Ordering',
  requests: 'Requests',
  other: 'Other',
};

const CATEGORY_COLORS: Record<OpsCategory, string> = {
  admin: '#6366F1',
  maintenance: '#F59E0B',
  ordering: '#0891B2',
  requests: '#8B5CF6',
  other: '#6B7280',
};

const ROW_HEIGHT = 36;
const LEFT_PANEL_WIDTH = 280;

interface OpsGanttProps {
  tasks: OpsTask[];
  teamMembers: CRMUser[];
  /** Hub filter signature (View As, category, etc.) — recenters timeline when it changes. */
  timelineScopeKey: string;
  onSelectTask: (task: OpsTask) => void;
  onUpdateTask: (id: string, updates: Partial<OpsTask>) => void;
}

type DragType = 'move' | 'resize-end' | 'resize-start';

const OpsGantt: React.FC<OpsGanttProps> = ({ tasks, timelineScopeKey, onSelectTask, onUpdateTask }) => {
  const [zoom, setZoom] = useState<ZoomLevel>('day');
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const ganttRootRef = useRef<HTMLDivElement>(null);
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const leftBodyScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingVerticalScrollRef = useRef(false);
  const timelineScrollingRef = useRef(false);
  const timelineScrollIdleRef = useRef<number | null>(null);
  const [smoothCellWidth, setSmoothCellWidth] = useState(ZOOM_CONFIGS.day.cellWidth);
  const smoothCellWidthRef = useRef(ZOOM_CONFIGS.day.cellWidth);
  const zoomAnimRafRef = useRef<number | null>(null);
  const timelineWheelZoomAnchorRef = useRef<{ viewportX: number; ratio: number; prevScrollLeft: number } | null>(
    null,
  );
  const fitDaysLenForSyncRef = useRef(0);
  const todayScrollForZoomRef = useRef<ZoomLevel | null>(null);
  const pinnedRangeRef = useRef(computeGanttDateRangeMs({ taskTimes: [] }));

  const [dragging, setDragging] = useState<{
    taskId: string;
    type: DragType;
    startX: number;
    origStart: Date;
    origEnd: Date;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ taskId: string; start: Date; end: Date } | null>(null);
  const dragPreviewRef = useRef<{ taskId: string; start: Date; end: Date } | null>(null);
  const dragPointerMovedRef = useRef(false);
  const suppressSelectUntilRef = useRef(0);

  const cancelZoomAnim = useCallback(() => {
    if (zoomAnimRafRef.current != null) {
      cancelAnimationFrame(zoomAnimRafRef.current);
      zoomAnimRafRef.current = null;
    }
  }, []);

  const animateCellWidthTo = useCallback(
    (to: number, duration = ZOOM_ANIM_MS) => {
      cancelZoomAnim();
      const from = smoothCellWidthRef.current;
      const clamped = Math.max(FIT_CELL_MIN, Math.min(FIT_CELL_MAX, to));
      if (Math.abs(from - clamped) < 0.4) {
        smoothCellWidthRef.current = clamped;
        setSmoothCellWidth(clamped);
        return;
      }
      const t0 = performance.now();
      const step = (now: number) => {
        const u = Math.min(1, (now - t0) / duration);
        const eased = 1 - (1 - u) ** 3;
        const v = from + (clamped - from) * eased;
        smoothCellWidthRef.current = v;
        setSmoothCellWidth(v);
        if (u < 1) {
          zoomAnimRafRef.current = requestAnimationFrame(step);
        } else {
          zoomAnimRafRef.current = null;
        }
      };
      zoomAnimRafRef.current = requestAnimationFrame(step);
    },
    [cancelZoomAnim],
  );

  const cellWidth = smoothCellWidth;
  const showDayNumbersInHeader = zoom === 'day' || (zoom === 'fit' && cellWidth >= 20);

  const ganttTasks = useMemo(() => tasks.filter(t => getOpsTaskSchedule(t) !== null), [tasks]);

  const computedRange = useMemo(() => {
    const taskTimes: unknown[] = [];
    ganttTasks.forEach(t => {
      const sched = getOpsTaskSchedule(t);
      if (sched) {
        taskTimes.push(sched.start, sched.end);
      }
    });
    return computeGanttDateRangeMs({ taskTimes });
  }, [ganttTasks]);

  if (computedRange.hasAnchors || !pinnedRangeRef.current.hasAnchors) {
    pinnedRangeRef.current = computedRange;
  }
  const rangeBounds = computedRange.hasAnchors ? computedRange : pinnedRangeRef.current;

  const days = useMemo(
    () => buildGanttDays(rangeBounds.startMs, rangeBounds.endMs),
    [rangeBounds.startMs, rangeBounds.endMs],
  );

  const months = useMemo(() => {
    const result: { label: string; startIdx: number; days: number }[] = [];
    let prevKey = '';
    days.forEach((d, i) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== prevKey) {
        result.push({ label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), startIdx: i, days: 1 });
        prevKey = key;
      } else {
        result[result.length - 1].days++;
      }
    });
    return result;
  }, [days]);

  const todayIdx = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return days.findIndex(d => d.getTime() === now.getTime());
  }, [days]);

  const scrollTimelineToAnchor = useCallback(() => {
    const el = scrollRef.current;
    if (!el || days.length === 0) return;

    let anchorIdx = todayIdx;
    if (anchorIdx < 0 && ganttTasks.length > 0) {
      const base = days[0].getTime();
      let minOffset = Number.POSITIVE_INFINITY;
      ganttTasks.forEach(t => {
        const sched = getOpsTaskSchedule(t);
        if (!sched) return;
        const offset = Math.round((stripOpsDay(sched.start).getTime() - base) / 86400000);
        if (offset >= 0 && offset < minOffset) minOffset = offset;
      });
      if (Number.isFinite(minOffset)) anchorIdx = minOffset;
    }
    if (anchorIdx < 0) anchorIdx = 0;

    const cw = smoothCellWidthRef.current;
    const maxScroll = Math.max(0, days.length * cw - el.clientWidth);
    const next = Math.min(maxScroll, Math.max(0, anchorIdx * cw - el.clientWidth / 3));
    el.scrollLeft = next;
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = next;
    el.scrollTop = 0;
    if (leftBodyScrollRef.current) leftBodyScrollRef.current.scrollTop = 0;
  }, [days, ganttTasks, todayIdx]);

  useEffect(() => {
    todayScrollForZoomRef.current = null;
    fitDaysLenForSyncRef.current = 0;
    requestAnimationFrame(() => scrollTimelineToAnchor());
  }, [timelineScopeKey, scrollTimelineToAnchor]);

  const handleZoomToFit = useCallback(() => {
    const el = scrollRef.current;
    if (!el || days.length === 0) return;
    const w = el.clientWidth;
    const cw = ganttFitCellWidth(w, days.length, FIT_CELL_MIN, FIT_CELL_MAX);
    setZoom('fit');
    animateCellWidthTo(cw);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = 0;
      if (headerScrollRef.current) headerScrollRef.current.scrollLeft = 0;
    });
  }, [days.length, animateCellWidthTo]);

  const handleToggleGanttFullscreen = useCallback(async () => {
    const root = ganttRootRef.current;
    if (!root) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const elWebkit = root as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    const isFs =
      document.fullscreenElement === root || doc.webkitFullscreenElement === root;
    try {
      if (isFs) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else await doc.webkitExitFullscreen?.();
      } else if (root.requestFullscreen) {
        await root.requestFullscreen();
      } else if (elWebkit.webkitRequestFullscreen) {
        await Promise.resolve(elWebkit.webkitRequestFullscreen());
      }
    } catch {
      /* blocked or unsupported */
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      const root = ganttRootRef.current;
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      setFullscreenActive(
        root != null &&
          (document.fullscreenElement === root || doc.webkitFullscreenElement === root),
      );
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  useEffect(() => {
    if (zoom === 'fit') {
      todayScrollForZoomRef.current = 'fit';
      return;
    }
    const el = scrollRef.current;
    if (!el || todayIdx < 0) return;
    if (todayScrollForZoomRef.current === zoom) return;
    todayScrollForZoomRef.current = zoom;
    const cw = ZOOM_CONFIGS[zoom].cellWidth;
    const scrollTo = todayIdx * cw - el.clientWidth / 3;
    el.scrollLeft = Math.max(0, scrollTo);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft;
  }, [todayIdx, zoom]);

  useEffect(() => {
    if (zoom !== 'fit') {
      fitDaysLenForSyncRef.current = days.length;
      return;
    }
    if (fitDaysLenForSyncRef.current === days.length) return;
    fitDaysLenForSyncRef.current = days.length;
    const el = scrollRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const n = Math.max(1, days.length);
    const cw = ganttFitCellWidth(w, n, FIT_CELL_MIN, FIT_CELL_MAX);
    if (Math.abs(cw - smoothCellWidthRef.current) < 0.5) return;
    cancelZoomAnim();
    smoothCellWidthRef.current = cw;
    setSmoothCellWidth(cw);
  }, [zoom, days.length, cancelZoomAnim]);

  useEffect(() => {
    if (zoom !== 'fit') return;
    const el = scrollRef.current;
    if (!el) return;
    let lastW = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (timelineScrollingRef.current) return;
      const w = el.clientWidth;
      if (!ganttFitViewportChangedEnough(lastW, w)) return;
      lastW = w;
      const n = Math.max(1, days.length);
      const cw = ganttFitCellWidth(w, n, FIT_CELL_MIN, FIT_CELL_MAX);
      if (Math.abs(cw - smoothCellWidthRef.current) < 0.5) return;
      cancelZoomAnim();
      smoothCellWidthRef.current = cw;
      setSmoothCellWidth(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, days.length, cancelZoomAnim]);

  useEffect(() => {
    if (zoom === 'fit') return;
    animateCellWidthTo(ZOOM_CONFIGS[zoom].cellWidth);
  }, [zoom, animateCellWidthTo]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const n = Math.max(1, days.length);
      if (n === 0) return;
      const factor = e.deltaY > 0 ? 0.97 : 1.03;
      const base = smoothCellWidthRef.current;
      const next = Math.max(FIT_CELL_MIN, Math.min(FIT_CELL_MAX, base * factor));
      if (Math.abs(next - base) < 1e-6) return;

      const rect = el.getBoundingClientRect();
      const viewportX = Math.max(0, Math.min(el.clientWidth, e.clientX - rect.left));
      const ratio = next / base;
      timelineWheelZoomAnchorRef.current = {
        viewportX,
        ratio,
        prevScrollLeft: el.scrollLeft,
      };

      setZoom('fit');
      cancelZoomAnim();
      smoothCellWidthRef.current = next;
      setSmoothCellWidth(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [days.length, cancelZoomAnim]);

  useLayoutEffect(() => {
    const anchor = timelineWheelZoomAnchorRef.current;
    if (!anchor) return;
    timelineWheelZoomAnchorRef.current = null;

    const el = scrollRef.current;
    if (!el) return;
    const n = Math.max(1, days.length);
    const cw = smoothCellWidthRef.current;
    const maxScroll = Math.max(0, n * cw - el.clientWidth);
    const { viewportX: vx, ratio, prevScrollLeft } = anchor;
    const nextScroll = prevScrollLeft * ratio + vx * (ratio - 1);
    el.scrollLeft = Math.max(0, Math.min(maxScroll, nextScroll));

    const head = headerScrollRef.current;
    if (head) head.scrollLeft = el.scrollLeft;
  }, [smoothCellWidth, days.length]);

  useEffect(
    () => () => {
      if (zoomAnimRafRef.current != null) cancelAnimationFrame(zoomAnimRafRef.current);
    },
    [],
  );

  const handleTimelineScroll = useCallback(() => {
    if (!scrollRef.current) return;
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
    if (!isSyncingVerticalScrollRef.current && leftBodyScrollRef.current) {
      isSyncingVerticalScrollRef.current = true;
      leftBodyScrollRef.current.scrollTop = scrollRef.current.scrollTop;
      isSyncingVerticalScrollRef.current = false;
    }
    timelineScrollingRef.current = true;
    if (timelineScrollIdleRef.current != null) {
      window.clearTimeout(timelineScrollIdleRef.current);
    }
    timelineScrollIdleRef.current = window.setTimeout(() => {
      timelineScrollIdleRef.current = null;
      timelineScrollingRef.current = false;
    }, 160);
  }, []);

  const handleLeftBodyScroll = useCallback(() => {
    if (!leftBodyScrollRef.current || !scrollRef.current) return;
    if (isSyncingVerticalScrollRef.current) return;
    isSyncingVerticalScrollRef.current = true;
    scrollRef.current.scrollTop = leftBodyScrollRef.current.scrollTop;
    isSyncingVerticalScrollRef.current = false;
  }, []);

  const dayIndex = useCallback(
    (d: Date) => {
      const base = days[0];
      if (!base) return 0;
      const target = stripOpsDay(d);
      return Math.round((target.getTime() - stripOpsDay(base).getTime()) / 86400000);
    },
    [days],
  );

  const flatRows = useMemo(() => {
    const rows: ({ type: 'category'; cat: OpsCategory; count: number } | { type: 'task'; task: OpsTask })[] = [];
    const categories = Object.keys(CATEGORY_LABELS) as OpsCategory[];
    for (const cat of categories) {
      const catTasks = ganttTasks.filter(t => t.category === cat);
      if (catTasks.length === 0) continue;
      rows.push({ type: 'category', cat, count: catTasks.length });
      if (!collapsedCats.has(cat)) {
        catTasks.forEach(t => rows.push({ type: 'task', task: t }));
      }
    }
    return rows;
  }, [ganttTasks, collapsedCats]);

  const totalWidth = days.length * cellWidth;

  const resolveBarDates = useCallback(
    (task: OpsTask): { start: Date; end: Date } | null => {
      if (dragPreview && dragPreview.taskId === task.id) {
        return { start: dragPreview.start, end: dragPreview.end };
      }
      const sched = getOpsTaskSchedule(task);
      if (!sched) return null;
      return { start: sched.start, end: sched.end };
    },
    [dragPreview],
  );

  const handleMouseDown = (e: React.MouseEvent, taskId: string, type: DragType) => {
    e.preventDefault();
    e.stopPropagation();
    dragPointerMovedRef.current = false;
    const task = ganttTasks.find(t => t.id === taskId);
    const sched = task ? getOpsTaskSchedule(task) : null;
    if (!sched) return;
    const init = { taskId, start: sched.start, end: sched.end };
    dragPreviewRef.current = init;
    setDragPreview(init);
    setDragging({
      taskId,
      type,
      startX: e.clientX,
      origStart: sched.start,
      origEnd: sched.end,
    });
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      const cw = smoothCellWidthRef.current;
      const dx = e.clientX - dragging.startX;
      if (Math.abs(dx) > 4) dragPointerMovedRef.current = true;
      const daysDelta = Math.round(dx / cw);

      if (dragging.type === 'move') {
        const newStart = new Date(dragging.origStart);
        newStart.setDate(newStart.getDate() + daysDelta);
        const newEnd = new Date(dragging.origEnd);
        newEnd.setDate(newEnd.getDate() + daysDelta);
        const next = { taskId: dragging.taskId, start: newStart, end: newEnd };
        dragPreviewRef.current = next;
        setDragPreview(next);
      } else if (dragging.type === 'resize-start') {
        const newStart = new Date(dragging.origStart);
        newStart.setDate(newStart.getDate() + daysDelta);
        const endT = stripOpsDay(dragging.origEnd).getTime();
        const nsT = stripOpsDay(newStart).getTime();
        if (nsT > endT) newStart.setTime(dragging.origEnd.getTime());
        const next = { taskId: dragging.taskId, start: newStart, end: dragging.origEnd };
        dragPreviewRef.current = next;
        setDragPreview(next);
      } else {
        const newEnd = new Date(dragging.origEnd);
        newEnd.setDate(newEnd.getDate() + daysDelta);
        if (stripOpsDay(newEnd).getTime() >= stripOpsDay(dragging.origStart).getTime()) {
          const next = { taskId: dragging.taskId, start: dragging.origStart, end: newEnd };
          dragPreviewRef.current = next;
          setDragPreview(next);
        }
      }
    };
    const handleUp = () => {
      const preview = dragPreviewRef.current;
      if (preview && preview.taskId === dragging.taskId) {
        const os = stripOpsDay(dragging.origStart).getTime();
        const oe = stripOpsDay(dragging.origEnd).getTime();
        const ps = stripOpsDay(preview.start).getTime();
        const pe = stripOpsDay(preview.end).getTime();
        const datesMoved = os !== ps || oe !== pe;
        if (datesMoved || dragPointerMovedRef.current) {
          suppressSelectUntilRef.current = Date.now() + 450;
        }
        if (datesMoved) {
          onUpdateTask(dragging.taskId, { startDate: preview.start, dueDate: preview.end });
        }
      }
      dragPreviewRef.current = null;
      setDragPreview(null);
      setDragging(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, onUpdateTask]);

  const handleBarClick = (task: OpsTask) => {
    if (Date.now() < suppressSelectUntilRef.current) return;
    onSelectTask(task);
  };

  return (
    <div className="ops-gantt" ref={ganttRootRef}>
      <div className="ops-gantt-controls">
        <div className="ops-gantt-controls-zoom">
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'month' ? 'active' : ''}`} onClick={() => setZoom('month')}>Month</button>
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'week' ? 'active' : ''}`} onClick={() => setZoom('week')}>Week</button>
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'day' ? 'active' : ''}`} onClick={() => setZoom('day')}>Day</button>
          <button
            type="button"
            className={`pm-btn pm-btn-sm ${zoom === 'fit' ? 'active' : ''}`}
            onClick={handleZoomToFit}
            title="Fit the full date range to the timeline width (updates when the panel is resized)"
          >
            Fit
          </button>
          <button
            type="button"
            className="pm-btn pm-btn-sm ops-gantt-fullscreen-btn"
            onClick={() => void handleToggleGanttFullscreen()}
            title={fullscreenActive ? 'Exit fullscreen' : 'Fullscreen timeline'}
            aria-pressed={fullscreenActive}
          >
            {fullscreenActive ? <Minimize2 size={16} strokeWidth={2.25} aria-hidden /> : <Maximize2 size={16} strokeWidth={2.25} aria-hidden />}
            <span className="ops-gantt-fullscreen-btn-label">{fullscreenActive ? 'Exit' : 'Fullscreen'}</span>
          </button>
        </div>
      </div>

      <div className="ops-gantt-container">
        <div className="ops-gantt-left" style={{ width: LEFT_PANEL_WIDTH }}>
          <div className="ops-gantt-left-header">
            <span>Task</span>
            <span>Status</span>
          </div>
          <div className="ops-gantt-left-body pm-scroll" ref={leftBodyScrollRef} onScroll={handleLeftBodyScroll}>
            {flatRows.length === 0 ? (
              <div className="ops-gantt-empty" style={{ height: ROW_HEIGHT * 2 }}>
                No scheduled tasks for this view. Add a due date on a task to show it here.
              </div>
            ) : (
              flatRows.map(row => {
                if (row.type === 'category') {
                  const isCol = collapsedCats.has(row.cat);
                  return (
                    <div key={`cat-${row.cat}`} className="ops-gantt-left-group" style={{ height: ROW_HEIGHT }}>
                      <div className="ops-gantt-left-group-color" style={{ background: CATEGORY_COLORS[row.cat] }} />
                      <button type="button" className="ops-gantt-left-toggle" onClick={() => {
                        setCollapsedCats(prev => {
                          const n = new Set(prev);
                          if (n.has(row.cat)) n.delete(row.cat); else n.add(row.cat);
                          return n;
                        });
                      }}>
                        {isCol ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <span className="ops-gantt-left-group-name">{CATEGORY_LABELS[row.cat]}</span>
                      <span className="ops-gantt-left-count">{row.count}</span>
                    </div>
                  );
                }
                return (
                  <div
                    key={`t-${row.task.id}`}
                    className="ops-gantt-left-task"
                    style={{ height: ROW_HEIGHT }}
                    onClick={() => onSelectTask(row.task)}
                  >
                    <span className="ops-gantt-left-task-name">{row.task.title}</span>
                    <div className="ops-gantt-left-task-status">
                      <StatusPill status={row.task.status} readonly />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="ops-gantt-right">
          <div className="ops-gantt-header" ref={headerScrollRef}>
            <div style={{ width: totalWidth, minWidth: totalWidth }}>
              <div className="ops-gantt-months">
                {months.map((m, i) => (
                  <div key={i} className="ops-gantt-month" style={{ width: m.days * cellWidth, left: m.startIdx * cellWidth }}>{m.label}</div>
                ))}
              </div>
              <div className="ops-gantt-days">
                {days.map((d, i) => {
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = i === todayIdx;
                  return (
                    <div key={i} className={`ops-gantt-day ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''}`} style={{ width: cellWidth }}>
                      {showDayNumbersInHeader && d.getDate()}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="ops-gantt-body pm-scroll" ref={scrollRef} onScroll={handleTimelineScroll}>
            <div style={{ width: totalWidth, minWidth: totalWidth, position: 'relative', minHeight: Math.max(flatRows.length, 1) * ROW_HEIGHT }}>
              {days.map((d, i) => {
                if (d.getDay() !== 0 && d.getDay() !== 6) return null;
                return (
                  <div key={`bg-${i}`} className="ops-gantt-weekend-col" style={{ left: i * cellWidth, width: cellWidth, height: Math.max(flatRows.length, 1) * ROW_HEIGHT }} />
                );
              })}

              {todayIdx >= 0 && (
                <div className="ops-gantt-today-line" style={{ left: todayIdx * cellWidth + cellWidth / 2, height: Math.max(flatRows.length, 1) * ROW_HEIGHT }} />
              )}

              {flatRows.map(row => {
                if (row.type === 'category') {
                  return <div key={`gr-${row.cat}`} className="ops-gantt-row ops-gantt-row-group" style={{ height: ROW_HEIGHT }} />;
                }
                const task = row.task;
                const barDates = resolveBarDates(task);
                if (!barDates) return null;
                const startIdx = dayIndex(barDates.start);
                const endIdx = dayIndex(barDates.end);
                const barLeft = startIdx * cellWidth;
                const barWidth = Math.max((endIdx - startIdx + 1) * cellWidth, cellWidth);
                const color = CATEGORY_COLORS[task.category] || '#6B7280';
                const isDragging = dragging?.taskId === task.id;

                return (
                  <div key={`tr-${task.id}`} className="ops-gantt-row" style={{ height: ROW_HEIGHT }}>
                    <div
                      className={`ops-gantt-bar ${isDragging ? 'dragging' : ''}`}
                      style={{ left: barLeft, width: barWidth, top: 6, height: ROW_HEIGHT - 12, background: color }}
                      onClick={() => handleBarClick(task)}
                      title={`${task.title}\n${barDates.start.toLocaleDateString()} — ${barDates.end.toLocaleDateString()}\nDrag to move · edges to change duration`}
                    >
                      <div
                        className="ops-gantt-bar-resize ops-gantt-bar-resize-start"
                        onMouseDown={e => handleMouseDown(e, task.id!, 'resize-start')}
                      />
                      <div
                        className="ops-gantt-bar-move"
                        onMouseDown={e => handleMouseDown(e, task.id!, 'move')}
                      >
                        {barWidth > 60 && <span className="ops-gantt-bar-label">{task.title}</span>}
                      </div>
                      <div
                        className="ops-gantt-bar-resize ops-gantt-bar-resize-end"
                        onMouseDown={e => handleMouseDown(e, task.id!, 'resize-end')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsGantt;
